<template>
  <div class="">
    <div class="map-container">
      <MapControls showList />
      <div class="map-content">
        <div id='map' ref="map" class="mapbox m-0 p-0" style="height: 90vh"></div>
        <div v-if="miniCoordsBar" class="coordsbar">
          <span class="material-icons">my_location</span> {{ miniCoordsBar }}
        </div>
        <MapButtons :buttons="buttons" :button-clicked="buttonClicked" />
      </div>
      <BaseMapStylesDialog />
      <LoadingDialog :show-loading-dialog="loading" />
      <WaypointDialog />
      <TrailDataCard />
      <MapDataPOIDetailDialog />
      <Loader v-if="miniLoader" />
      <WaypointAddingNotification />
    </div>
  </div>
</template>

<script lang="ts">

import { defineComponent } from 'vue';
import LoadingDialog from '../../utils/shared_components/LoadingDialog.vue';
import BaseMapStylesDialog from ".././base_map_dialog/BaseMapStylesDialog.vue";
import { mapState } from "vuex";
import MapButtons from "../components/MapButtons.vue";
import { OnBoardingGoal } from "../../utils/models/OnBoardingGoal";
import MapControls from "../components/MapControls.vue";
import WaypointDialog from "../waypoint_dialog/WaypointDialog.vue";
import WaypointAddingNotification from "../waypoint_adding_mode_notificaiton/WaypointAddingNotification.vue";
import {ThreeDimensionalMapManager} from "../../utils/models/MapStyles";
import {RevenueCatWrapper} from "../../utils/RevenueCatWrapper";
import TrailDataCard from "../trail_data_card/TrailDataCard.vue";
import Loader from "../../utils/shared_components/Loader.vue";
import axiosClient from "../../utils/helpers/axios_client";
import emitter from "../../utils/bus";
import mapboxgl from "mapbox-gl";
import {HiikerMapStyle} from "../../utils/models/MapStyles";
import createGeoLocate from "../../utils/helpers/GeoLocateHelper";
import { Modal } from 'bootstrap';
import {LayerRegistry} from "../../utils/layer-registry";
import MapDataPOIDetailDialog from "../map_data_poi_dialog/MapDataPOIDetailDialog.vue";

export default defineComponent({
  async beforeCreate() {
    this.$store.dispatch('parkMap/initialiseStore');
    emitter.on('user:updated', async () => {
      const response = await axiosClient.get('/user/current');
      // @ts-ignore
      this.user = response.data;
      // @ts-ignore
      window.user = this.user;
    });
  },
  async mounted() {
    mapboxgl.accessToken = "pk.eyJ1IjoiaGlpa2VyLW1hcHMiLCJhIjoiY2x0YThyZHppMWhmejJqbzExODBuY3dveiJ9.nBrtnXgbzxpAx_953HCB-w"
    const map = new mapboxgl.Map(this.$store.getters['parkMap/initialMapSettings']);
    const isProOrProPlus = await RevenueCatWrapper.isProOrProPlus()
    map.setStyle(HiikerMapStyle.getCurrentStyle(isProOrProPlus).styleUrl)
    window.map = map;
    window.layerRegistry = new LayerRegistry(window.map);
    const canvas = map.getCanvasContainer();
    canvas.style.cursor = "crosshair";
    window.map.on('style.load', async () => {
      await this.$store.dispatch('parkMap/mapInitialised');
      await this.$store.dispatch('parkMap/getPark');
      await this.$store.dispatch('mapDataPOIs/addParkPois');
      await this.$store.dispatch('waypoints/initialiseStore');
      await this.$store.dispatch('mapStyles/initialiseStore');
    });
    map.on('load', () => {
      emitter.emit('map-loaded');
      createGeoLocate();
    });
  },
  components: {
    MapDataPOIDetailDialog,
    Loader,
    TrailDataCard,
    WaypointAddingNotification, WaypointDialog,
    MapControls,
    MapButtons,
    LoadingDialog,
    BaseMapStylesDialog,
  },
  data() {
    return {
      hover: false,
      buttons: [
        {
          id: 0,
          name: this.$t('shared.maps'),
          icon: 'layers',
          enabled: false,
          dropIcon: true,
        },
        {
          id: 1,
          name: this.$t('park_map.3d_maps'),
          icon: 'landscape',
          enabled: false,
          dropIcon: false,
        },
        {
          id: 2, name: this.$t('park_map.waypoints'), icon: 'location_on', enabled: false, dropIcon: false,
        },
        {
          id: 4, name: this.$t('park_map.add_to_map'), icon: 'add_circle', enabled: false, dropIcon: false,
        },
      ],
    };
  },
  setup() {
    return {};
  },
  computed: Object.assign({}, mapState({
    miniCoordsBar: state => state.parkMap.miniCoordsBar,
    loading: state => state.parkMap.loading,
    miniLoader: state => state.mapDataOverlays?.miniLoader,
  })),
  watch: {
    _3dEnabled: function (newVal, oldVal) {
      this.buttons[3].enabled = newVal
    }
  },
  methods: {
    user()  {
      return (window.user && window.user.firebase_user_id) ? window.user : null;
    },
    async buttonClicked(id: number) {
      const isProOrProPlus = await RevenueCatWrapper.isProOrProPlus()
      switch (id) {
        case 0:
          if (this.user()) {
            const element = document.getElementById("map-styles-dialog");
            const modal = new Modal(element);
            modal.show();
          } else {
            if (window.onBoardingVue) {
              window.onBoardingVue.launchFor(OnBoardingGoal.MapStyles)
            } else {
              import('../on_boarding/main').then(() => {
                window.onBoardingVue.launchFor(OnBoardingGoal.MapStyles)
              })
            }
          }
          break;
        case 1:
          if (isProOrProPlus) {
            ThreeDimensionalMapManager.toggle3DMode()
          } else {
            window.location.href = "/purchases"
          }
          break;
        case 2:
          if (window.user) {
            const element = document.getElementById("waypoint-options-dialog");
            const modal = new Modal(element);
            modal.show();
          } else {
            window.onBoardingVue.launchFor(OnBoardingGoal.Waypoints)
          }
          break;
        case 3:
          this.$store.dispatch('poi/removeAllPOIsFromMap')
        case 4:
          if (window.user) {
            const element = document.getElementById("add-to-map-dialog");
            const modal = new Modal(element);
            modal.show();
          } else {
            window.onBoardingVue.launchFor(OnBoardingGoal.Waypoints)
          }
          break;
      }
    },
  },
});
</script>

<style>

.mapboxgl-ctrl {
  display: none;
}

.map-container {
  flex: 1 1 auto;
  max-width: 100%;
  z-index: 7;
}

.map-content {
  width: 100%;
  height: 100%;
  position: relative;
}

.backButton {
  background-color: #202020bb;
  top: 20px;
  left: 5%;
  border-radius: 4px;
  z-index: 100;
}

.chart-div {
  bottom: 0;
  width: 100%;
  height: auto;
  position: absolute;
  z-index: 100;
  background-color: rgb(0 0 0 / 74%);
}

.button-container {
  width: fit-content;
  top: 20px;
  right: 2%;
}

.mapboxgl-ctrl-top-left {
  left: 0;
  top: unset;
  bottom: 0;
}

.btn-success {
  color: #fff;
}

@media (max-width: 900px) {
  .map-title-trail{
    top: unset;
  }

  .button-container {
    width: 100%;
    left: 0;
    right: unset;
  }

  .backButton {
    left: 14%;
  }
}

@media (max-width: 500px) {
  .button-container {
    top: 0;
  }
}

.mapboxgl-ctrl-bottom-left {
  bottom: 10px !important;
}
</style>
